import { createFileRoute, Link } from '@tanstack/react-router';
import { Header, Footer } from '@/components/marketing';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Shield, Lock, Eye, Server, CheckCircle2, ArrowRight } from 'lucide-react';

export const Route = createFileRoute('/security')({
  component: SecurityPage,
});

const securityFeatures = [
  { icon: Lock, title: 'Encryption', description: 'All data is encrypted at rest using AES-256 and in transit using TLS 1.3.' },
  { icon: Eye, title: 'Access Controls', description: 'Role-based access control with granular permissions for team members.' },
  { icon: Server, title: 'Infrastructure', description: 'Hosted on SOC 2 compliant infrastructure with regular security audits.' },
  { icon: Shield, title: 'Code Isolation', description: 'Your code is processed in isolated containers and never shared.' },
];

const certifications = ['SOC 2 Type II', 'GDPR Compliant', 'ISO 27001', 'CCPA Compliant'];

function SecurityPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 pt-24 pb-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge variant="secondary" className="mb-4">Security</Badge>
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl mb-4">Enterprise-Grade Security</h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Your code is your most valuable asset. We protect it with industry-leading security practices.
            </p>
          </div>
          <section className="mb-24">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {securityFeatures.map((feature) => {
                const Icon = feature.icon;
                return (
                  <Card key={feature.title}>
                    <CardHeader>
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                        <Icon className="h-6 w-6 text-primary" />
                      </div>
                      <CardTitle className="text-lg">{feature.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground">{feature.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>
          <section className="mb-24">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl font-bold text-center mb-8">Compliance & Certifications</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                {certifications.map((cert) => (
                  <div key={cert} className="flex items-center gap-3 p-4 border rounded-lg">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="font-medium">{cert}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
          <section className="mb-24">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl font-bold text-center mb-8">Security Practices</h2>
              <div className="space-y-6">
                <div className="border-l-4 border-primary pl-4">
                  <h3 className="font-semibold mb-2">Regular Penetration Testing</h3>
                  <p className="text-muted-foreground">We conduct quarterly penetration tests with third-party security firms to identify and address vulnerabilities.</p>
                </div>
                <div className="border-l-4 border-primary pl-4">
                  <h3 className="font-semibold mb-2">Vulnerability Disclosure Program</h3>
                  <p className="text-muted-foreground">We maintain a responsible disclosure program and reward security researchers who help us improve.</p>
                </div>
                <div className="border-l-4 border-primary pl-4">
                  <h3 className="font-semibold mb-2">Employee Security Training</h3>
                  <p className="text-muted-foreground">All team members undergo regular security awareness training and follow strict data handling procedures.</p>
                </div>
                <div className="border-l-4 border-primary pl-4">
                  <h3 className="font-semibold mb-2">Incident Response</h3>
                  <p className="text-muted-foreground">We have a documented incident response plan with 24/7 monitoring and rapid response capabilities.</p>
                </div>
              </div>
            </div>
          </section>
          <section>
            <Card className="bg-gradient-to-br from-primary/10 to-transparent border-primary/20 max-w-2xl mx-auto">
              <CardContent className="py-12 text-center">
                <h2 className="text-2xl font-bold mb-4">Security Questions?</h2>
                <p className="text-muted-foreground max-w-xl mx-auto mb-6">
                  Our security team is available to answer questions and provide documentation for enterprise security reviews.
                </p>
                <Button asChild>
                  <Link to="/contact" search={{ type: 'enterprise' }}>Contact Security Team<ArrowRight className="ml-2 h-4 w-4" /></Link>
                </Button>
              </CardContent>
            </Card>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
